/**
 * Content model for the marketing mega-menu navigation.
 *
 * Pure data — no JSX — so the nav shell, the desktop panels and the mobile
 * list all render from one source of truth. Copy lives in the `landingNav.mm`
 * i18n namespace (EN + NL in lockstep); this module only holds the structure:
 * i18n key prefixes, destinations and monochrome lucide icons.
 *
 * Claims rule (docs/ai/fixcontrol-website-claims.md): only shipped providers
 * appear here — no Azure DevOps, and Kubernetes/Argo copy stays within the
 * deployment-gate capabilities the engine actually has. Every destination is
 * an existing page or homepage anchor; `{locale}` in an href is replaced with
 * the active locale at render time (used for the docs deep-links).
 */

import type { LucideIcon } from "lucide-react";
import {
  Boxes,
  Bot,
  Bug,
  Building2,
  Compass,
  Container,
  FileSearch,
  FlaskConical,
  GitBranch,
  Github,
  Gitlab,
  CalendarDays,
  History,
  Layers,
  LifeBuoy,
  Mail,
  MessagesSquare,
  Radio,
  Rocket,
  ScrollText,
  ShieldCheck,
  Slack,
  SquareKanban,
  Ticket,
  UserCheck,
  UserCog,
  Users,
  Workflow,
} from "lucide-react";

/** A single teaching card: icon, title, one-sentence value line. */
export type MenuCardItem = {
  /** i18n key prefix under `landingNav.mm` — resolves `.title` and `.desc`. */
  key: string;
  href: string;
  icon: LucideIcon;
};

export type IntegrationColumn = {
  /** i18n key under `landingNav.mm.integrations` for the column heading. */
  key: string;
  items: MenuCardItem[];
};

/** Top-level entries: mega menus and plain links, in display order. */
export type NavEntry =
  | { kind: "menu"; id: MenuId; labelKey: string }
  | { kind: "link"; id: string; labelKey: string; href: string };

export type MenuId = "product" | "use-cases" | "integrations" | "security" | "about";

export const NAV_ENTRIES: NavEntry[] = [
  { kind: "menu", id: "product", labelKey: "landingNav.product" },
  { kind: "menu", id: "use-cases", labelKey: "landingNav.useCases" },
  { kind: "menu", id: "integrations", labelKey: "landingNav.integrations" },
  { kind: "menu", id: "security", labelKey: "landingNav.safety" },
  { kind: "link", id: "pricing", labelKey: "landingNav.pricing", href: "/pricing" },
  { kind: "menu", id: "about", labelKey: "landingNav.mm.about.label" },
  { kind: "link", id: "contact", labelKey: "landingNav.contact", href: "/contact" },
];

/* ── Product ──────────────────────────────────────────────────────────── */

export const PRODUCT_CARDS: MenuCardItem[] = [
  {
    key: "product.supportIntelligence",
    href: "/use-cases/slack-jira-ticket-questions",
    icon: LifeBuoy,
  },
  { key: "product.operationalGovernance", href: "/security#governance", icon: ShieldCheck },
  {
    key: "product.evidenceEngine",
    href: "/use-cases/ai-agent-approval-ledger",
    icon: FileSearch,
  },
  { key: "product.changeValidation", href: "/product/change-validation", icon: FlaskConical },
  { key: "product.commandCenter", href: "/product#platform", icon: MessagesSquare },
  { key: "product.missionTimeline", href: "/#example", icon: History },
];

/** “Why teams choose FixControl” checklist — i18n keys under mm.product.why. */
export const PRODUCT_WHY_KEYS = [
  "humanApproval",
  "explainable",
  "auditTrail",
  "multiTenant",
  "kubernetes",
] as const;

export const PRODUCT_CTA = { key: "product.cta", href: "/product" };

/* ── Use-cases ────────────────────────────────────────────────────────── */

export const USE_CASE_CARDS: MenuCardItem[] = [
  {
    key: "useCases.supportEmail",
    href: "/use-cases/support",
    icon: Mail,
  },
  {
    key: "useCases.engineeringBugs",
    href: "/use-cases/engineering",
    icon: Bug,
  },
  {
    key: "useCases.customerSupport",
    href: "/use-cases/freshdesk-jira-approved-replies",
    icon: MessagesSquare,
  },
  {
    key: "useCases.supportToEngineering",
    href: "/use-cases/support-to-engineering-remediation",
    icon: Workflow,
  },
  {
    key: "useCases.deploymentGovernance",
    href: "/use-cases/ai-deployment-governance",
    icon: Rocket,
  },
  {
    key: "useCases.operationalAi",
    href: "/use-cases/ai-agent-approval-ledger",
    icon: Bot,
  },
  {
    key: "useCases.hrQuestions",
    href: "/use-cases/hr-governed-answers",
    icon: UserCog,
  },
];

/* ── Integrations ─────────────────────────────────────────────────────── */

export const INTEGRATION_COLUMNS: IntegrationColumn[] = [
  {
    key: "colSupport",
    items: [
      { key: "integrations.slack", href: "/integrations/slack", icon: Slack },
      { key: "integrations.teams", href: "/integrations/teams", icon: Users },
      { key: "integrations.freshdesk", href: "/integrations/freshdesk", icon: LifeBuoy },
      { key: "integrations.odoo", href: "/integrations/odoo", icon: Ticket },
      { key: "integrations.topdesk", href: "/integrations/topdesk", icon: MessagesSquare },
    ],
  },
  {
    key: "colHr",
    items: [
      { key: "integrations.afas", href: "/integrations/afas", icon: CalendarDays },
      { key: "integrations.youforce", href: "/integrations/youforce", icon: UserCheck },
    ],
  },
  {
    key: "colDevelopment",
    items: [
      { key: "integrations.jira", href: "/integrations/jira", icon: SquareKanban },
      { key: "integrations.github", href: "/integrations/github", icon: Github },
      { key: "integrations.gitlab", href: "/integrations/gitlab", icon: Gitlab },
    ],
  },
  {
    key: "colInfrastructure",
    items: [
      { key: "integrations.kubernetes", href: "/integrations/argo", icon: Container },
      { key: "integrations.argo", href: "/integrations/argo", icon: GitBranch },
      { key: "integrations.fcagent", href: "/integrations/fc-agent", icon: Radio },
    ],
  },
];

/* ── Security ─────────────────────────────────────────────────────────── */

// The four pillars deep-link into the /security marketing page; the CTA goes
// one level deeper, to the security docs.
export const SECURITY_CARDS: MenuCardItem[] = [
  { key: "security.tenantIsolation", href: "/security#tenant-isolation", icon: Boxes },
  { key: "security.governance", href: "/security#governance", icon: ShieldCheck },
  { key: "security.evidence", href: "/security#evidence", icon: FileSearch },
  { key: "security.auditability", href: "/security#auditability", icon: ScrollText },
];

export const SECURITY_CTA = { key: "security.cta", href: "/docs/{locale}/admin/security" };

/* ── About ────────────────────────────────────────────────────────────── */

export const ABOUT_ITEMS: MenuCardItem[] = [
  { key: "about.mission", href: "/about#mission", icon: Compass },
  { key: "about.principles", href: "/about#principles", icon: UserCheck },
  { key: "about.architecture", href: "/about#architecture", icon: Layers },
  { key: "about.company", href: "/about#company", icon: Building2 },
];

/** Resolve `{locale}` placeholders in docs deep-links. */
export function resolveHref(href: string, locale: string): string {
  return href.replace("{locale}", locale);
}
